import {
  ReactNode,
  createContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { useQuery } from '@apollo/client'
import { ICourseModules, ICourseModulesNodes } from '@dtos/Course'
import { useGetModules } from '@services/api/graphql/modules'
import { GET_MODULE } from '@services/api/graphql/queries/getModules'
import {
  COURSE_CONTENTS_PAGE_SIZE,
  MODULES_PAGE_SIZE,
  SUBMODULES_PAGE_SIZE,
  hasMoreSubmodulesForModule,
  isEmptyPageError,
} from '@utils/submodulesPagination'
import { useApolloErrorToast } from '@hooks/useApolloErrorToast'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'

interface IModulesProviderProps {
  modules?: ICourseModules
  isLoading: boolean
  error?: Error
  onLoadMore?: () => void
  onLoadMoreSubmodules?: (moduleId: number) => Promise<void>
  hasMoreSubmodules?: (module: ICourseModulesNodes) => boolean
  getModules?: (args: {
    variables: {
      offset: number | undefined
      limit?: number
      moduleId?: string
      submodulesOffset?: number
      submodulesLimit?: number
      courseContentsOffset?: number
      courseContentsLimit?: number
    }
  }) => Promise<{
    data: {
      course: {
        courseModules: ICourseModules | null
      }
    }
    error?: { message?: string }
  }>
  resetModules?: () => void
}

interface IUseGetModulesInterface {
  getModules: (args: {
    variables: {
      offset: number | undefined
      limit?: number
      moduleId?: string
      submodulesOffset?: number
      submodulesLimit?: number
      courseContentsOffset?: number
      courseContentsLimit?: number
    }
  }) => Promise<{
    data: {
      course: {
        courseModules: ICourseModules | null
      }
    }
    error?: { message?: string }
  }>
  data: {
    course: {
      courseModules: ICourseModules | null
    }
  } | null
  loading: boolean
  error: Error | undefined
}

const MODULES_VARIABLES = {
  offset: 0,
  limit: MODULES_PAGE_SIZE,
  submodulesOffset: 0,
  submodulesLimit: SUBMODULES_PAGE_SIZE,
  courseContentsOffset: 0,
  courseContentsLimit: COURSE_CONTENTS_PAGE_SIZE,
} as const

export const ModulesContext = createContext<IModulesProviderProps>(
  {} as IModulesProviderProps,
)

export function ModulesProvider({ children }: { children: ReactNode }) {
  const [modulesState, setModulesState] = useState<ICourseModules | undefined>()
  // Módulos que já devolveram uma página incompleta de submódulos — não há mais
  // o que buscar. Precisa ser state (não ref) para desarmar o sentinel de
  // scroll no re-render, senão ele fica pedindo a mesma página vazia.
  const [exhaustedSubmodules, setExhaustedSubmodules] = useState<Set<number>>(
    () => new Set(),
  )
  const submodulesInFlightRef = useRef<Set<number>>(new Set())
  const showApolloErrorToast = useApolloErrorToast()
  const { courseId } = useCourseRouteParams()

  // Initial data — fires immediately on mount, benefits from Apollo cache
  const {
    data: initialData,
    loading: initialLoading,
    error,
  } = useQuery(GET_MODULE, {
    variables: {
      id: `${courseId}`,
      ...MODULES_VARIABLES,
    },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-only',
  })

  // Lazy query for pagination (handleLoadMore, handleLoadMoreSubmodules)
  const { getModules } = useGetModules({
    id: `${courseId}`,
    ...MODULES_VARIABLES,
  }) as IUseGetModulesInterface

  // Set initial state from useQuery data
  useEffect(() => {
    if (initialData?.course?.courseModules && !modulesState) {
      const uniqueModules = {
        ...initialData.course.courseModules,
        nodes: initialData.course.courseModules.nodes?.filter(
          (module: { id: number }, index: number, self: { id: number }[]) =>
            index === self.findIndex((m) => m.id === module.id),
        ),
      }
      setModulesState(uniqueModules)
    }
  }, [initialData, modulesState])

  // Refs for stable callbacks — event handlers read current values via refs
  const modulesStateRef = useRef(modulesState)
  modulesStateRef.current = modulesState
  const loadingRef = useRef(initialLoading)
  loadingRef.current = initialLoading

  const resetModules = useCallback(() => {
    setModulesState(undefined)
    setExhaustedSubmodules(new Set())
    submodulesInFlightRef.current = new Set()
  }, [])

  const markSubmodulesExhausted = useCallback((moduleId: number) => {
    setExhaustedSubmodules((prev) => {
      if (prev.has(moduleId)) return prev
      const next = new Set(prev)
      next.add(moduleId)
      return next
    })
  }, [])

  const hasMoreSubmodules = useCallback(
    (module: ICourseModulesNodes) =>
      hasMoreSubmodulesForModule(module, exhaustedSubmodules),
    [exhaustedSubmodules],
  )

  const handleLoadMore = useCallback(async () => {
    if (loadingRef.current) return
    const currentState = modulesStateRef.current
    const { data } = await getModules({
      variables: {
        offset: currentState?.nodes?.length || 0,
        submodulesOffset: 0,
        submodulesLimit: SUBMODULES_PAGE_SIZE,
        courseContentsOffset: 0,
        courseContentsLimit: COURSE_CONTENTS_PAGE_SIZE,
      },
    })
    if (data?.course.courseModules && currentState) {
      const uniqueModules = {
        ...currentState,
        nodes: [
          ...currentState.nodes,
          ...data.course.courseModules.nodes,
        ].filter(
          (module, index, self) =>
            index === self.findIndex((m) => m.id === module.id),
        ),
      }
      setModulesState(uniqueModules)
    }
  }, [getModules])

  const handleLoadMoreSubmodules = useCallback(
    async (moduleId: number) => {
      const currentState = modulesStateRef.current
      if (!currentState || loadingRef.current) return

      const module = currentState.nodes.find((m) => m.id === moduleId)
      if (!module) return
      if (exhaustedSubmodules.has(moduleId)) return
      // O sentinel de scroll dispara em rajada; sem isso a mesma página é
      // pedida várias vezes e o offset (derivado do tamanho atual) fica stale.
      if (submodulesInFlightRef.current.has(moduleId)) return
      submodulesInFlightRef.current.add(moduleId)

      const currentSubmodulesCount = module.courseModules?.length || 0

      try {
        const { data, error } = await getModules({
          variables: {
            moduleId: `${moduleId}`,
            offset: 0,
            limit: 1,
            submodulesOffset: currentSubmodulesCount,
            submodulesLimit: SUBMODULES_PAGE_SIZE,
            courseContentsOffset: 0,
            courseContentsLimit: COURSE_CONTENTS_PAGE_SIZE,
          },
        })

        if (error) {
          // Só "Empty page!" (offset além do fim) encerra a lista. Erro
          // transitório mantém o sentinel armado para o próximo scroll
          // tentar de novo — marcar exhausted aqui truncava a lista de
          // submódulos permanentemente por causa de um blip de rede.
          if (isEmptyPageError(error)) markSubmodulesExhausted(moduleId)
          console.error(error)
          return
        }

        const newSubmodules =
          data?.course.courseModules?.nodes?.[0]?.courseModules

        // Página incompleta = fim da lista. É o único sinal disponível: o core
        // não expõe a contagem total de submódulos.
        if (!newSubmodules || newSubmodules.length < SUBMODULES_PAGE_SIZE) {
          markSubmodulesExhausted(moduleId)
        }

        if (newSubmodules?.length) {
          setModulesState((prev) => {
            if (!prev) return prev

            return {
              ...prev,
              nodes: prev.nodes.map((mod) => {
                if (mod.id === moduleId) {
                  const existing = mod.courseModules || []
                  const all = [...existing, ...newSubmodules]
                  const unique = all.filter(
                    (sub, idx, self) =>
                      idx === self.findIndex((s) => s.id === sub.id),
                  )
                  return { ...mod, courseModules: unique }
                }
                return mod
              }),
            }
          })
        }
      } catch (err) {
        // "Empty page!" do core (offset além do fim) pode chegar como exceção —
        // também significa fim da lista. Qualquer outro erro é transitório e
        // não encerra a paginação.
        if (isEmptyPageError(err)) markSubmodulesExhausted(moduleId)
        console.error(err)
      } finally {
        submodulesInFlightRef.current.delete(moduleId)
      }
    },
    [getModules, exhaustedSubmodules, markSubmodulesExhausted],
  )

  useEffect(() => {
    if (error && error.message !== 'Empty page!') {
      showApolloErrorToast(error, 'apolloErrors.lessonExtraContents.title')
    }
  }, [error, showApolloErrorToast])

  // Derive isLoading to avoid including `initialLoading` in contextValue deps
  const isLoading = initialLoading && !modulesState

  const contextValue = useMemo(
    () => ({
      modules: modulesState,
      isLoading,
      error,
      onLoadMore: handleLoadMore,
      onLoadMoreSubmodules: handleLoadMoreSubmodules,
      hasMoreSubmodules,
      getModules,
      resetModules,
    }),
    [
      modulesState,
      isLoading,
      error,
      handleLoadMore,
      handleLoadMoreSubmodules,
      hasMoreSubmodules,
      getModules,
      resetModules,
    ],
  )

  return (
    <ModulesContext.Provider value={contextValue}>
      {children}
    </ModulesContext.Provider>
  )
}
